import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { useCallback, useMemo, useState } from "react";

import { formatDistance } from "../../Workplace/useDistanceToWorkplace";
import { useGetPaginatedPlacementWorkplaces } from "../api/useGetPaginatedPlacementWorkplaces";

export interface PlacementWorkplaceListItemData {
  workplaceId: string;
  name: string;
  formattedDistance: string;
  placementsCount: number;
}

export function usePlacementWorkplaceSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const logDebouncedSearchTerm = useCallback((value: string) => {
    const normalizedSearchTerm = value.trim();

    if (normalizedSearchTerm === "") {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.BROWSE_PLACEMENT_WORKPLACES_SEARCHED, {
      searchTerm: normalizedSearchTerm,
    });
  }, []);
  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    debounceTimeInMs: 500,
    onUpdateValue: logDebouncedSearchTerm,
  });

  const { userId: workerId } = useDefinedWorker();
  const workerGeoLocation = useWorkerGeoLocation();

  const {
    data: workplacesData,
    isLoading,
    isSuccess,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetPaginatedPlacementWorkplaces({
    workerId,
    filter: {
      workplaceName: debouncedSearchTerm || undefined,
      coordinates: workerGeoLocation,
    },
  });

  const workplaces: PlacementWorkplaceListItemData[] = useMemo(
    () =>
      workplacesData?.pages?.flatMap((page) =>
        page.data.map((workplace) => ({
          workplaceId: workplace.id,
          name: workplace.attributes.name,
          formattedDistance: formatDistance(workplace.attributes.distance),
          placementsCount: workplace.attributes.placementsCount,
        }))
      ) ?? [],
    [workplacesData?.pages]
  );

  const totalCount = workplacesData?.pages?.[0]?.meta.totalCount ?? 0;

  const handleLoadMore = useCallback(async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    isLoading,
    isSuccess,
    isError,
    workplaces,
    totalCount,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  };
}
